import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { AlertTriangle, ArrowRight, ShieldAlert } from "lucide-react";

/**
 * Mandatory Introduction to Trading — gate every student clears once before
 * the curriculum unlocks. Includes the "education only, no financial advice"
 * disclaimer as an explicit acknowledgment.
 */
export default function IntroPage() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Admins skip. Students who already acknowledged go straight to dashboard.
    if (!user) return;
    if (user.role === "admin" || user.intro_acknowledged_at) {
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);

  const acknowledge = async () => {
    if (!checked) return;
    setBusy(true);
    try {
      await client.post("/intro/acknowledge");
      if (refreshUser) await refreshUser();
      toast.success("Curriculum unlocked. Welcome to the desk.");
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to acknowledge");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <p className="overline text-[#D4AF37]">◇ Before you sit at the desk</p>
        <h1 className="font-display text-5xl md:text-6xl mt-2 leading-[1.05]">
          Introduction to Trading.
        </h1>
        <p className="text-neutral-500 text-lg mt-4 font-body max-w-xl">
          A five-minute read. Once. Nothing you'll learn afterwards makes sense without it.
        </p>

        <div className="gold-divider my-10 max-w-md" />

        {/* Section 1 */}
        <section className="mb-12">
          <p className="overline mb-3">§ 01 · The Framing</p>
          <p className="text-neutral-200 font-body leading-[1.9] text-[15px]">
            Trading is not investing. It is not saving. It is not a hobby. It is a
            business of <span className="text-[#D4AF37]">managing uncertainty for a return</span> —
            the return coming from other participants who read the same information incorrectly, too late,
            or too emotionally.
          </p>
          <p className="text-neutral-200 font-body leading-[1.9] text-[15px] mt-4">
            Every trade is a transfer of capital between two convictions. For you to
            win, someone else has to lose — and the market's dominant participants
            (banks, funds, algos) have far deeper pockets, better data, and faster infrastructure than you.
            Your only edges are <span className="text-[#D4AF37]">patience</span>, <span className="text-[#D4AF37]">selectivity</span>, and <span className="text-[#D4AF37]">unshakable process</span>.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-12">
          <p className="overline mb-3">§ 02 · What This Course Covers</p>
          <ul className="space-y-3 text-neutral-200 font-body text-[15px] leading-[1.7]">
            <li className="pl-5 border-l-2 border-[#D4AF37]">
              <span className="text-white">Eight levels · Forty-nine modules</span> — foundation to institutional execution across Forex, Futures & Commodities.
            </li>
            <li className="pl-5 border-l-2 border-[#D4AF37]/60">
              Market structure, delivery mechanics (FVG · IFVG · CISD), liquidity, sessions, and how institutions actually price.
            </li>
            <li className="pl-5 border-l-2 border-[#D4AF37]/60">
              Risk sizing, R:R, drawdown discipline, and prop-firm ready processes.
            </li>
            <li className="pl-5 border-l-2 border-[#D4AF37]/60">
              Weekly Boardroom outlooks, live gold intelligence, and admin-verified certification when you clear every module.
            </li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-12">
          <p className="overline mb-3">§ 03 · The Three Pillars</p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { n: "01", t: "Knowledge", d: "You cannot execute what you cannot read. Every module builds a skill. Skip nothing." },
              { n: "02", t: "Discipline", d: "Rules exist to be followed when you don't feel like it. That's the entire game." },
              { n: "03", t: "Capital",   d: "Preserve first. Grow second. A trader who quits at -20% is out. One who caps at -3% is still working." },
            ].map((p) => (
              <div key={p.n} className="border border-[#1f1f1f] p-5 bg-[#0a0a0a]">
                <p className="text-[#D4AF37] font-mono text-xs tracking-widest">{p.n}</p>
                <p className="font-display text-2xl mt-2">{p.t}</p>
                <p className="text-neutral-400 text-[13px] font-body leading-relaxed mt-2">{p.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — DISCLAIMER (required) */}
        <section className="mb-10 border-2 border-[#D4AF37] p-6 bg-[#1a1408]" data-testid="intro-disclaimer">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert size={20} className="text-[#D4AF37]" />
            <p className="overline text-[#D4AF37]">§ 04 · Education Only — Important</p>
          </div>
          <p className="font-display text-2xl md:text-3xl mb-4 leading-tight">
            This course provides <span className="text-[#D4AF37]">education only</span>. It is <span className="text-[#D4AF37]">not financial advice</span>.
          </p>
          <ul className="space-y-2 text-neutral-200 font-body text-[14px] leading-[1.75]">
            <li className="flex gap-2"><AlertTriangle size={14} className="text-[#D4AF37] shrink-0 mt-1.5" />
              Nothing in The Boardroom curriculum, outlooks, live intelligence, or admin communications constitutes a personal recommendation, solicitation, or offer to buy or sell any financial instrument.</li>
            <li className="flex gap-2"><AlertTriangle size={14} className="text-[#D4AF37] shrink-0 mt-1.5" />
              Trading Forex, Futures and Commodities carries a high level of risk and may not be suitable for every investor. You can lose more than your initial deposit.</li>
            <li className="flex gap-2"><AlertTriangle size={14} className="text-[#D4AF37] shrink-0 mt-1.5" />
              Past performance — of markets, strategies, or any Boardroom outlook — is <em>never</em> a guarantee of future results.</li>
            <li className="flex gap-2"><AlertTriangle size={14} className="text-[#D4AF37] shrink-0 mt-1.5" />
              You are solely responsible for your trades. Consult a licensed financial adviser before making any investment decision.</li>
            <li className="flex gap-2"><AlertTriangle size={14} className="text-[#D4AF37] shrink-0 mt-1.5" />
              The Boardroom, its founders, and its authors accept no liability for any loss arising from the use of this material.</li>
          </ul>
        </section>

        {/* Acknowledgment */}
        <label className="flex items-start gap-3 cursor-pointer select-none mb-6" data-testid="intro-checkbox-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="intro-checkbox"
            className="mt-1.5 h-4 w-4 accent-[#D4AF37]"
          />
          <span className="text-neutral-200 font-body text-[14px] leading-relaxed">
            I have read the Introduction to Trading and I understand that <span className="text-[#D4AF37]">The Boardroom provides education only, not financial advice</span>. I accept full responsibility for my own trading decisions.
          </span>
        </label>

        <button
          onClick={acknowledge}
          disabled={!checked || busy}
          data-testid="intro-acknowledge-btn"
          className="btn-gold text-xs uppercase tracking-[0.3em] px-8 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Unlocking…" : (<span className="inline-flex items-center gap-2">Enter The Boardroom <ArrowRight size={13} /></span>)}
        </button>

        <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-8">
          ◇ You will only see this page once.
        </p>
      </div>
    </div>
  );
}
